import { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const MeasurementsSidebar = ({
  totalDistance,
  measuredAngle,
  polygonArea,
  selectedMeshVolume,
  measurements,
  onSave,
  onDelete,
  handleCheckboxChange,
}) => {
  const [editIndex, setEditIndex] = useState(null);
  const [editedText, setEditedText] = useState("");

  return (
    <div
      className="absolute right-0 top-12 w-80 bg-slate-50/95 backdrop-blur-xl border-l border-slate-200/60 shadow-2xl z-20 h-[calc(100%-3rem)] flex flex-col transition-all duration-300"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div className="bg-gradient-to-r from-[var(--main-color)] to-[var(--main-color-bold)] text-white p-3 flex items-center justify-center shadow-md flex-shrink-0 z-10">
        <span className="text-sm font-bold tracking-wider opacity-90">MEASUREMENTS</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-3 p-4">
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 flex flex-col items-center justify-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Distance</span>
            <span className="text-lg font-bold text-slate-800">{totalDistance} <span className="text-xs font-medium text-slate-500">mm</span></span>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 flex flex-col items-center justify-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Angle</span>
            <span className="text-lg font-bold text-slate-800">{measuredAngle} <span className="text-xs font-medium text-slate-500">deg</span></span>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 flex flex-col items-center justify-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Area</span>
            <span className="text-lg font-bold text-slate-800">{polygonArea || 0} <span className="text-xs font-medium text-slate-500">mm²</span></span>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 flex flex-col items-center justify-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Volume</span>
            <span className="text-lg font-bold text-slate-800">{Number(selectedMeshVolume ?? 0).toFixed(2)} <span className="text-xs font-medium text-slate-500">mm³</span></span>
          </div>
        </div>

        <ul className="list-none m-0 p-0 pb-4">
        {measurements.map((measurement, index) => (
          <li
            key={index}
            className="cursor-pointer mx-3 my-3 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
          >
            <div className="p-3.5 flex justify-between items-center w-full border-b border-slate-50">
              {editIndex === index ? (
                <input
                  type="text"
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-gray-800 focus:outline-none focus:ring focus:border-blue-500"
                />
              ) : (
                <div>
                  <div className="font-medium md:font-normal md:text-sm text-gray-800">
                    {measurement.name}
                  </div>
                  <div className="text-xs md:font-light text-gray-600 truncate">
                    {measurement.totalDistance} mm
                    {measurement.polygonArea && measurement.polygonArea > 0 && (
                      <span className="block">
                        Area: {measurement.polygonArea} mm²
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center">
              <label
                className="inline-flex items-center cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  value={measurement.show ? "true" : "false"}
                  className="sr-only peer"
                  checked={measurement.show}
                  onChange={(e) => {
                    handleCheckboxChange(e.target.checked, measurement.id);
                  }}
                />
                <div className="relative w-7 h-4 md:w-5 md:h-3 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full md:after:h-2 md:after:w-2 after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>

              <button
                onClick={() => {
                  if (editIndex === index) {
                    onSave(editedText, measurement.id);
                    setEditIndex(null);
                  } else {
                    setEditIndex(index);
                    setEditedText(measurement.name);
                  }
                }}
                className="px-2 py-1 md:p-0 bg-[var(--main-color)] text-white font-semibold md:font-normal text-xs rounded hover:bg-[var(--main-color-bold)] transition-colors duration-150 ease-in-out ml-2 md:h-8 md:w-8"
              >
                {editIndex === index ? <SaveIcon /> : <EditIcon />}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (editIndex === index) {
                    setEditIndex(null);
                  } else {
                    onDelete(index);
                  }
                }}
                className={`px-2 py-1 md:p-0 ${
                  editIndex === index
                    ? "bg-gray-500 hover:bg-gray-600"
                    : "bg-red-500 hover:bg-red-600"
                } text-white font-semibold md:font-normal text-xs rounded transition-colors duration-150 ease-in-out ml-2 md:h-8 md:w-8`}
              >
                {editIndex === index ? <CloseIcon /> : <DeleteOutlineIcon />}
              </button>
            </div>
          </li>
        ))}
        </ul>
      </div>
      {/* Footer buffer zone */}
      <div className="bg-slate-50 border-t border-slate-200 text-center p-3 flex-shrink-0 z-10">
        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">
          Powered by Wellztech
        </p>
      </div>
    </div>
  );
};

export default MeasurementsSidebar;
